import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ReportType =
  | 'revenue_by_period'
  | 'revenue_by_client'
  | 'revenue_by_job_type'
  | 'aged_receivables'
  | 'staff_productivity'
  | 'job_completion_rates'
  | 'lead_pipeline'
  | 'job_turnaround'
  | 'billing_summary'
  | 'tax_season_capacity'
  | 'custom';

export type ExportFormat = 'pdf' | 'csv' | 'excel';

export type ScheduleFrequency = 'daily' | 'weekly' | 'monthly';

export interface ReportFilter {
  dateFrom?: string;
  dateTo?: string;
  clientId?: string;
  staffMemberId?: string;
  jobType?: string;
  status?: string;
}

export interface ReportColumn {
  id: string;
  label: string;
  visible: boolean;
}

export interface SavedReport {
  id: string;
  name: string;
  reportType: ReportType;
  filters: ReportFilter;
  columns: ReportColumn[];
  query?: Record<string, unknown>;
  isScheduled: boolean;
  scheduleFrequency?: ScheduleFrequency;
  scheduleRecipients?: string[];
  createdAt: string;
  updatedAt: string;
  ownerName: string | null;
}

export interface ReportDataRow {
  [key: string]: string | number | boolean | null;
}

export interface ReportResult {
  columns: ReportColumn[];
  rows: ReportDataRow[];
  total: number;
  page: number;
  pageSize: number;
  summary?: Record<string, number>;
}

export interface GenerateReportParams {
  reportType: ReportType;
  filters: ReportFilter;
  columns?: string[];
  page?: number;
  pageSize?: number;
}

export interface SaveReportPayload {
  name: string;
  reportType: ReportType;
  filters: ReportFilter;
  columns: ReportColumn[];
  isScheduled?: boolean;
  scheduleFrequency?: ScheduleFrequency;
  scheduleRecipients?: string[];
}

export interface ExportReportPayload {
  reportType: ReportType;
  filters: ReportFilter;
  columns?: string[];
  format: ExportFormat;
}

export interface ChartDataPoint {
  label: string;
  value: number;
  secondaryValue?: number;
  color?: string;
}

export interface SavedReportsListParams {
  search?: string;
  source?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  enabled?: boolean;
}

export interface SavedReportsListResult {
  reports: SavedReport[];
  page: number;
  pageSize: number;
  total: number;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const reportKeys = {
  all: ['reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  list: (params: GenerateReportParams) => [...reportKeys.lists(), params] as const,
  saved: () => [...reportKeys.all, 'saved'] as const,
  savedList: (params: SavedReportsListParams) => [...reportKeys.saved(), 'list', params] as const,
  savedDetail: (id: string) => [...reportKeys.saved(), id] as const,
  chart: (reportType: ReportType, filters: ReportFilter) =>
    [...reportKeys.all, 'chart', reportType, filters] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Generate a report with the given parameters.
 * Requirements: 12.2, 12.4
 */
export function useGenerateReport(params: GenerateReportParams) {
  return useQuery({
    queryKey: reportKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.post<ApiResponse<ReportResult>>('/reports/generate', {
        reportType: params.reportType,
        filters: params.filters,
        columns: params.columns,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
      });
      return response.data.data;
    },
    enabled: !!params.reportType && params.reportType !== 'custom',
  });
}

/** Run a legacy predefined report on demand (saved report Run / modal). */
export function useRunLegacyReport() {
  return useMutation({
    mutationFn: async (params: GenerateReportParams) => {
      const response = await apiClient.post<ApiResponse<ReportResult>>('/reports/generate', {
        reportType: params.reportType,
        filters: params.filters,
        columns: params.columns,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
      });
      return response.data.data;
    },
  });
}

/**
 * Fetch chart data for a specific report type.
 * Requirements: 12.2
 */
export function useReportChart(reportType: ReportType, filters: ReportFilter) {
  return useQuery({
    queryKey: reportKeys.chart(reportType, filters),
    queryFn: async () => {
      const response = await apiClient.post<ApiResponse<ChartDataPoint[]>>('/reports/chart', {
        reportType,
        filters,
      });
      return response.data.data;
    },
    enabled: !!reportType,
  });
}

/**
 * Fetch all saved report configurations.
 * Requirements: 12.10
 */
export function useSavedReports(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.saved(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/reports/saved');
      return (response.data.data ?? []).map(mapSavedReport);
    },
    enabled: options?.enabled !== false,
  });
}

export function useSavedReportsList(params: SavedReportsListParams = {}) {
  const { enabled, search, source, page = 1, pageSize = 25, sortBy, sortDirection } = params;

  return useQuery({
    queryKey: reportKeys.savedList({ search, source, page, pageSize, sortBy, sortDirection }),
    queryFn: async (): Promise<SavedReportsListResult> => {
      const query = new URLSearchParams();
      query.set('page', String(page));
      query.set('pageSize', String(pageSize));
      if (search?.trim()) query.set('search', search.trim());
      if (source) query.set('source', source);
      if (sortBy) query.set('sortBy', sortBy);
      if (sortDirection) query.set('sortDirection', sortDirection);

      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination?: { page: number; pageSize: number; total: number };
      }>(`/reports/saved?${query.toString()}`);

      const rows = response.data.data ?? [];
      return {
        reports: rows.map(mapSavedReport),
        page: response.data.pagination?.page ?? page,
        pageSize: response.data.pagination?.pageSize ?? pageSize,
        total: response.data.pagination?.total ?? rows.length,
      };
    },
    enabled: enabled !== false,
    placeholderData: keepPreviousData,
  });
}

/** Maps a snake_case API response to the camelCase SavedReport interface. */
export function mapSavedReport(r: Record<string, unknown>): SavedReport {
  const config = (r.config ?? {}) as Record<string, unknown>;
  return {
    id: r.id as string,
    name: r.name as string,
    reportType: (r.report_type || config.report_type || 'custom') as ReportType,
    filters: (config.filters ?? r.filters ?? {}) as ReportFilter,
    columns: (config.columns ?? []) as ReportColumn[],
    query: (config.query as Record<string, unknown>) ?? undefined,
    isScheduled: (r.is_scheduled ?? false) as boolean,
    scheduleFrequency: ((r.schedule_config as Record<string, unknown>)?.frequency ?? undefined) as
      | ScheduleFrequency
      | undefined,
    scheduleRecipients: ((r.schedule_config as Record<string, unknown>)?.recipients ??
      undefined) as string[] | undefined,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
    ownerName:
      (r.owner_name as string | undefined) || (r.owner_email as string | undefined) || null,
  };
}

/**
 * Save a report configuration.
 * Requirements: 12.10
 */
export function useSaveReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveReportPayload) => {
      const response = await apiClient.post<ApiResponse<SavedReport>>('/reports/saved', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
    },
  });
}

/**
 * Delete a saved report configuration.
 * Requirements: 12.10
 */
export function useDeleteSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: string) => {
      await apiClient.delete(`/reports/saved/${reportId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
    },
  });
}

/**
 * Export a report in the specified format.
 * Requirements: 12.5
 */
export function useExportReport() {
  return useMutation({
    mutationFn: async (payload: ExportReportPayload) => {
      const response = await apiClient.post('/reports/export', payload, {
        responseType: 'blob',
      });
      // Trigger download
      const blob = new Blob([response.data as BlobPart]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = payload.format === 'excel' ? 'xlsx' : payload.format;
      link.download = `report-${payload.reportType}-${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ─── Report Type Metadata ───────────────────────────────────────────────────

export interface ReportTypeOption {
  value: ReportType;
  label: string;
  description: string;
  category: 'financial' | 'operational' | 'pipeline';
}

export const REPORT_TYPES: ReportTypeOption[] = [
  {
    value: 'revenue_by_period',
    label: 'Revenue by Period',
    description: 'Revenue breakdown by month, quarter, or year',
    category: 'financial',
  },
  {
    value: 'revenue_by_client',
    label: 'Revenue by Client',
    description: 'Revenue generated per client',
    category: 'financial',
  },
  {
    value: 'revenue_by_job_type',
    label: 'Revenue by Job Type',
    description: 'Revenue breakdown by job type (T1, T2, Bookkeeping, etc.)',
    category: 'financial',
  },
  {
    value: 'aged_receivables',
    label: 'Aged Receivables',
    description: 'Outstanding invoices grouped by aging buckets',
    category: 'financial',
  },
  {
    value: 'billing_summary',
    label: 'Billing Summary',
    description: 'Invoiced, collected, write-offs, and outstanding by period',
    category: 'financial',
  },
  {
    value: 'staff_productivity',
    label: 'Staff Productivity',
    description: 'Hours logged, jobs completed, and utilization per staff member',
    category: 'operational',
  },
  {
    value: 'job_completion_rates',
    label: 'Job Completion Rates',
    description: 'Completion rates and average turnaround by job type',
    category: 'operational',
  },
  {
    value: 'job_turnaround',
    label: 'Job Turnaround',
    description: 'Average completion time by job type and staff member',
    category: 'operational',
  },
  {
    value: 'tax_season_capacity',
    label: 'Tax Season Capacity',
    description: 'Projected workload vs available staff hours',
    category: 'operational',
  },
  {
    value: 'lead_pipeline',
    label: 'Lead Pipeline',
    description: 'Conversion rates, time-to-convert, and source effectiveness',
    category: 'pipeline',
  },
];
